import { EmailsIcon } from "@minket/brand/icons/emails";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { UnavailableAction } from "@/components/page/action";
import { GhostSections } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { TestModeStamp } from "@/components/page/stamp";
import { CancelEmailAction } from "@/components/sending/cancel-action";
import { detailSections, emailQuery } from "@/components/sending/email-detail";
import { EmailStatus } from "@/components/sending/status";
import type { EmailDetail as Detail } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { useWorkspace } from "@/lib/workspace";

const EmailDetailView = lazy(() =>
  import("@/components/sending/email-detail.ondemand").then((module) => ({
    default: module.EmailDetailView,
  })),
);

export const Route = createFileRoute("/emails/$id")({
  component: EmailDetail,
});

/** The page's one action: cancelling, while the email is still scheduled. */
function CancelWhileScheduled({ email, projectId }: { email: Detail; projectId: string }) {
  const canCancel = useCan("emails.cancel");
  if (email.status !== "scheduled") return null;
  if (!canCancel) {
    return <UnavailableAction label="Cancel scheduled send" reason="Viewers cannot cancel email" />;
  }
  return <CancelEmailAction projectId={projectId} emailId={email.id} />;
}

/** Under the head's rule: the email's status, and Live beside it (test mode is on the band). */
function StatusLine({ email }: { email: Detail }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[15px] text-foreground">
      <EmailStatus status={email.status} className="font-semibold" />
      {email.mode === "live" ? (
        <span className="rounded-sm border px-[7px] text-xs leading-[18px] font-semibold whitespace-nowrap">
          Live
        </span>
      ) : null}
    </span>
  );
}

function EmailDetail() {
  const { id } = Route.useParams();
  const { project } = useWorkspace();
  const query = useQuery({ ...emailQuery(project?.id ?? "", id), enabled: project !== null });
  const email = query.data;
  const test = email?.mode === "test";
  const subject = email ? email.subject || "No subject" : "Email";
  return (
    <Page
      title={subject}
      documentTitle={email ? `${subject} · Emails` : `Email ${id}`}
      serial={{ id, label: "email id" }}
      back={
        test
          ? { to: "/sandbox", label: "Test-mode emails" }
          : { to: "/emails", label: "Sent emails" }
      }
      specimen={test}
      stamp={test ? <TestModeStamp /> : undefined}
      description={email ? <StatusLine email={email} /> : undefined}
      action={
        email && project ? <CancelWhileScheduled email={email} projectId={project.id} /> : null
      }
    >
      <ProjectScope
        will="This page will show the email's chain of custody, its message and content, and the webhook deliveries of its events."
        icon={<EmailsIcon size={20} />}
        preview={(busy) => <GhostSections sections={detailSections} busy={busy} />}
      >
        {(scoped) => (
          <Suspense fallback={<GhostSections sections={detailSections} busy={false} />}>
            <EmailDetailView projectId={scoped.id} emailId={id} />
          </Suspense>
        )}
      </ProjectScope>
    </Page>
  );
}
